import React from 'react';
import type { Facility } from '../data/constants.js';
import { SVG_COLORS } from '../data/constants.js';
import { formatNum, getInitials } from '../data/helpers.js';
import './facility-card.css';

interface FacilityCardProps {
  facility: Facility;
  isExpanded: boolean;
  onToggle: () => void;
  loading?: boolean;
}

export function FacilityCard({
  facility,
  isExpanded,
  onToggle,
  loading,
}: FacilityCardProps): React.ReactElement {
  const totalProfiles = facility.surgeons.reduce((a, s) => a + s.profiles, 0);
  const totalRecorded = facility.surgeons.reduce((a, s) => a + s.recorded, 0);
  const regionFlag = facility.region === 'US' ? '\u{1F1FA}\u{1F1F8}' : '\u{1F1EC}\u{1F1E7}';
  return (
    <div
      className={`mc-facility${isExpanded ? ' mc-facility--expanded' : ''}${loading ? ' mc-facility--loading' : ''}`}
    >
      {/* Header */}
      <div className="mc-facility__header" onClick={onToggle}>
        <div className="mc-facility__header-left">
          <span className="mc-facility__flag">{regionFlag}</span>
          <div>
            <div className="mc-facility__name-row">
              <span className="mc-facility__name">{facility.name}</span>
              <span className="mc-facility__status">{facility.status}</span>
            </div>
            <span className="mc-facility__meta">
              {facility.city} &middot; {facility.ors} ORs &middot; Since {facility.launchDate}
            </span>
          </div>
        </div>
        <div className="mc-facility__header-right">
          <div className="mc-facility__stat">
            <div className="mc-facility__stat-value">{facility.surgeons.length}</div>
            <div className="mc-facility__stat-label">SURGEONS</div>
          </div>
          <div className="mc-facility__stat">
            <div className="mc-facility__stat-value mc-facility__stat-value--accent">
              {formatNum(totalProfiles)}
            </div>
            <div className="mc-facility__stat-label">PROFILES</div>
          </div>
          <div className="mc-facility__stat">
            <div className="mc-facility__stat-value mc-facility__stat-value--accent">
              {formatNum(totalRecorded)}
            </div>
            <div className="mc-facility__stat-label">RECORDED</div>
          </div>
          <div className={`mc-facility__chevron${isExpanded ? ' mc-facility__chevron--open' : ''}`}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke={SVG_COLORS.accent}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Expanded surgeon table */}
      {isExpanded && (
        <div className="mc-facility__body">
          <div className="mc-facility__body-inner">
            {/* Specialties tags */}
            <div className="mc-facility__specialties">
              {facility.specialties.map((s) => (
                <span key={s} className="mc-facility__specialty-tag">
                  {s.toUpperCase()}
                </span>
              ))}
            </div>
            {/* Table header */}
            <div className="mc-facility__table-header">
              <span className="mc-facility__th">SURGEON</span>
              <span className="mc-facility__th">SPECIALTY</span>
              <span className="mc-facility__th mc-facility__th--right">PROFILES</span>
              <span className="mc-facility__th mc-facility__th--right">RECORDED</span>
            </div>
            {/* Surgeon rows */}
            {facility.surgeons.map((surgeon, idx) => (
              <div key={idx} className="mc-facility__table-row">
                <div className="mc-facility__surgeon-cell">
                  <div className="mc-facility__surgeon-avatar">{getInitials(surgeon.name)}</div>
                  <span className="mc-facility__surgeon-name">{surgeon.name}</span>
                </div>
                <div className="mc-facility__td mc-facility__td--specialty">
                  {surgeon.specialty}
                </div>
                <div className="mc-facility__td mc-facility__td--number">{surgeon.profiles}</div>
                <div className="mc-facility__td mc-facility__td--number">
                  {formatNum(surgeon.recorded)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
